import * as React from 'react';
import { badge as badgeCls, dot as dotCls } from './Badge.css';

export type BadgeTone =
  'neutral' | 'info' | 'success' | 'warn' | 'danger' | 'agent' | 'coordination';

export type BadgeVariant = 'pill' | 'subtle' | 'solid' | 'compact' | 'dot';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Colour family. `coordination` (teal) denotes the Workstream Agent;
   * `agent` (purple) denotes system agents (Smith / Mira).
   */
  tone?: BadgeTone;
  /**
   * Shape: `pill` (default, 20px radius — the prototype's primary badge),
   * `subtle` (alias of `pill`, kept for legacy callers), `solid` (bold
   * tone fill), `compact` (4px radius, regular weight — the inline chat-
   * header "Agent" badge), `dot` (a leading status dot + label).
   */
  variant?: BadgeVariant;
  /** `sm` 18px / `md` 20px / `lg` 22px tall. Default `md`. */
  size?: BadgeSize;
  icon?: React.ReactNode;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * Badge — small status pill or count chip. Seven tones (neutral / info /
 * success / warn / danger / agent / coordination), five variants
 * (`pill` default soft-fill / `subtle` legacy alias of pill / `solid`
 * filled / `compact` inline chat-header chip / `dot` status-dot +
 * label), three sizes (sm / md / lg). Used inline alongside text for
 * status hints, agent attribution, count badges, etc.
 */
export function Badge({
  tone = 'neutral',
  variant = 'pill',
  size = 'md',
  icon,
  children,
  ref,
  className,
  ...rest
}: BadgeProps) {
  const cls = [badgeCls({ tone, variant, size }), className].filter(Boolean).join(' ');
  return (
    <span ref={ref} className={cls} {...rest}>
      {variant === 'dot' && <span className={dotCls({ tone })} />}
      {icon}
      {children}
    </span>
  );
}
Badge.displayName = 'Badge';
